import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import { Model as DataModel, SortOptions } from '@/models/Model';
import SelectFilter from '@/components/select-filter/SelectFilter';
import debounce from 'debounce';
import { AxiosError } from 'axios';
import LoaderCard from '@/components/loader-card/LoaderCard';
import { sanitizeString } from '@/support/String';
import { Nmbr } from '@atabix/core';
import { isArray, isFunction, isObject } from 'lodash';

@Component<DataTable>({
  components: {
    SelectFilter,
    LoaderCard
  }
})
export default class DataTable extends Vue implements Omit<DatatableInterface, 'datatableMounted' | 'datatableInitialize'> {
  @Prop({ default: null })
  protected options!: TableOptions;

  @Prop({ default: 'id' })
  protected itemKey!: string;

  @Prop({ default: true })
  protected multiple!: boolean;

  @Prop({ default: false })
  protected compact!: boolean;

  @Prop({ default: false })
  protected editable!: boolean;
  protected initialized: boolean = false;

  @Prop({ default: '' })
  protected searchPlaceholder!: string;

  @Prop({ default: '' })
  protected foundTextSuffix!: string;

  @Prop({ default: '' })
  protected addTextPrefix!: string;

  @Prop({ default: '' })
  protected perPageTextSuffix!: string;

  @Prop({ default: '' })
  protected noDataText!: string;

  @Watch("options")
  public optionsChanged(): void {
    if (this.initialized) {
      this.initialize();
    }
  }

  @Prop({ default: null })
  protected limit!: number;
  protected $limit: number = 10;
  protected limitSelected: number = 10;
  protected editMode: boolean = false;
  protected modal: boolean = false;

  @Prop({ default: 1 })
  protected page!: number;
  protected $page: number = 1;

  protected items: DataModel[] = [];
  protected expandedItems: DataModel[] = [];
  protected changedItems: string[] = [];
  protected totals: Total[] = [];
  protected itemCache: any = {};
  protected total = 0;
  protected lastPage = 1;
  protected isLoading: boolean = true;
  protected showLoaderCard: boolean = false;

  protected TableHeader: object[] = [
    {
      text: "Name",
      value: "name"
    }
  ];

  protected pagination: object = {
    sortBy: "name",
    rowsPerPage: 5
  };

  protected name: TableName = {
    singular: "Item",
    plural: "Items"
  };

  protected $sort: undefined | SortOptions;

  @Prop({ default: () => {} })
  protected visibility!: TableVisibility;

  protected visible: TableVisibility = {
    search: true,
    total: true,
    limit: true,
    pagination: true,
    checkboxes: true,
    title: true,
    scrollable: false
  };

  @Prop({ default: false })
  protected clear!: boolean;

  @Prop({ default: () => [] })
  protected default!: string[];

  protected searchInput: string = '';

  protected tableActions: TableActions[] = [];
  protected tableFilters: TableFilters[] = [];

  public searchDebounce: Function = this.handleSearch();
  public paginationDebounce: Function = this.handlePagination();

  public selected: any = [];
  public selectedFilter: any = null;

  protected dateModals: object = {};

  public mounted(): void {
    if (this.default) {
      this.selected = this.default;
    } else {
      this.selected = [];
    }

    document.addEventListener("datatable:hook", (event: any) => {
      if (event.detail) {
        event.detail(this);
      }
    });

    this.initialize();
  }

  public initialize(): void {
    this.items = [];
    this.totals = [];
    this.total = 0;
    this.isLoading = true;
    this.$page = this.page;
    this.$limit = this.limit;
    this.limitSelected = this.$limit;
    (this.pagination as any).rowsPerPage = this.$limit;
    this.visible = { ...{}, ...this.visible, ...this.visibility };


    this.TableHeader = [];
    this.tableActions = [];
    this.tableFilters = [];

    if (this.options.headers !== undefined) {
      this.TableHeader = this.options.headers;
    }

    if (this.options.actions !== undefined) {
      this.tableActions = this.options.actions;

      const hasActions =
        this.TableHeader.findIndex(
          (header: any) => header.value === "$actions"
        ) !== -1;
      if (this.tableActions.length > 0 && !hasActions) {
        this.TableHeader.push({ text: "", value: "$actions" });
      }
    }

    if (this.options.filters !== undefined) {
      this.tableFilters = this.options.filters;
    }

    if (
      this.options.sort !== undefined &&
      this.options.sort.key !== undefined
    ) {
      this.$sort = this.options.sort as SortOptions;
    }

    if (this.options.name !== undefined) {
      this.name = { ...this.name, ...this.options.name };
    }
    this.fetchSource();
  }

  public fetchSource(dispatchToStore?: boolean): void {
    this.isLoading = true;

    if (this.$sort && this.$sort.key) {
      this.options.model.sort(this.$sort.key, this.$sort.order);
    }

    if (this.searchInput !== undefined) {
      this.options.model.filter("search", this.searchInput);
    }

    this.options.model
      .limit(this.$limit)
      .page(this.$page)
      .all()
      .then((items: DataModel[]) => {
        this.items = items;
        this.$emit("dataLoaded", this.items);
        this.total = items[0] !== undefined ? (items[0].meta as TableMeta).total : 0;
        this.lastPage = items[0] !== undefined ? (items[0].meta as TableMeta).last_page : 1;
        this.totals = items[0] !== undefined ? (items[0].meta as TableMeta).totals : [];
        this.createDateModals();

        if (dispatchToStore) {
          let payload: { [key: string]: DataModel[] } = {};
          payload[`${this.options.model.getInstanceName()}`] = items;
          this.$store.dispatch("updateFilters", payload);
        }

        this.initialized = true;
        this.isLoading = false;
      })
      .catch((error: AxiosError) => {
        this.isLoading = false;
      });
  }

  public createDateModals(): void {
    const fields: string[] = [];
    this.options.headers.forEach((header: TableHeader, index: number) => {
      if ((header as any).type && (header as any).type === "date") {
        fields.push(header.value);
      }
    });

    this.items.forEach((item: DataModel) => {
      fields.forEach((field: string) => {
        const key = `${(item as any).id}_${field}`;
        this.$set(this.dateModals, key, false);
      });
    });
  }

  public select(items: any[]): void {
    this.selected = [...items, ...this.selected];
  }

  public unselect(items: any[]): void {
    this.selected = this.selected.filter((item: any) => {
      return !items.includes(item);
    });
  }

  public unselectAll(): void {
    this.selected = [];
  }

  public refresh(dispatchToStore?: boolean): void {
    this.fetchSource(dispatchToStore);
  }

  public deleteItem(item: DataModel): void {
    this.isLoading = true;
    item.delete().then((resonse: any) => {
      this.fetchSource();
    });
  }

  public toggleAll(): void {
    if (this.selected.length) {
      this.selected = [];
    } else {
      this.selected = this.items.slice();
    }
  }

  public handlePagination(): Function {
    return debounce(
      (page: number) => {
        this.$page = page;
        this.setPage(page);
        this.fetchSource();
      },
      300,
      true
    );
  }

  protected setPage(page: number): void {
    localStorage.setItem(`${this.$route.name}_page`, page.toString());
  }

  protected setLimit(limit: number): void {
    localStorage.setItem(`${this.$route.name}_limit`, limit.toString());
  }

  public handleAction(action: Function, item: DataModel, props: any): void {
    action(this, item, props);
  }

  public handleIcon(action: Function, item: DataModel): void {
    return action(this, item);
  }

  public setItemChanged(item: DataModel): void {
    if (!this.changedItems.includes((item as any).id)) {
      this.changedItems.push((item as any).id);
    }
  }

  public resetDateField(item: DataModel, property: string): void {
    if (!this.itemCache[(item as any).id]) {
      return;
    }
    (item as any)[property] = this.itemCache[(item as any).id][property];
    (this.dateModals as any)[`${(item as any).id}_${property}`] = false;
  }

  public cancelEdit(): void {
    Object.keys(this.itemCache).forEach((key: string) => {
      const item: any = this.items.find((level: any) => {
        return level.id === key;
      });

      if (!item) {
        return;
      }

      Object.keys(this.itemCache[key]).forEach((property: string) => {
        item[property] = this.itemCache[key][property];
      });
    });

    this.clearChangedItems();
    this.editMode = false;
  }

  public handleTableEdit(): void {}

  public minDate(header: any): any {
    if (!header.min) {
      return "";
    }
    return header.min;
  }

  public maxDate(header: any): any {
    if (!header.max) {
      return "";
    }
    return header.max;
  }


  protected getTotalValue(key: string, isCurrency = false) {
    const total = this.findTotal(key);

    if (!total) { return '' }

    if (!isCurrency) { return total.value; }

    return Nmbr.toCurrency(total.value)
  }

  protected getTotalPrefix(key: string) {
    const total = this.findTotal(key);

    return total ? total.prefix : '';
  }

  protected findTotal(key: string): Total | undefined {
    if (!this.totals) {
      return undefined;
    }

    let totals: Total[] | Record<string, unknown> | any = this.totals;

    if (!isArray(totals) && isObject(totals)) {
      const entries: Total[] = [];

      Object.entries(totals)
        .forEach((total) => entries.push({
          prefix: '',
          key: total[0],
          value: total[1],
        }));

      totals = entries;
    }

    return totals.find((total: Total) => key === total.key);
  }

  protected isVisible(visible: any, item: DataModel) {
    if (visible === undefined) {
      return true;
    }

    if (this.isFunction(visible)) {
      return visible(this, item);
    }

    return visible;
  }

  public handleSearch(): any {
    return debounce((searchInput: string) => {
      this.searchInput = searchInput;
      this.$page = 1;
      this.setPage(1);
      this.fetchSource();
    }, 400);
  }

  /**
   * datatable column headers can be printed out simply (header.breakTitle = false)
   * they can be split in lines by their \s (header.breakTitle = true & is NOT array header.breakTitleArray)
   * or they can be split in lines by defined strings (header.breakTitleArray =['sluitings','datum'])
   *
   * @param {*} header
   * @returns
   * @memberof DataTable
   */
  public createTitle(header: any): string {
    if (!header.breakTitle) return header.text;

    const start = "<span class='column-header-flex'>";
    const end = "</span>";
    if (Array.isArray(header.breakTitleArray)) {
      return (
        start +
        header.breakTitleArray.map(this.wrapWithColumnHeaderRow).join(``) +
        end
      );
    } else {
      return (
        start +
        header.text
          .split(" ")
          .map(this.wrapWithColumnHeaderRow)
          .join(``) +
        end
      );
    }
  }

  private wrapWithColumnHeaderRow(text: string): string {
    return `<span class='column-header-flex-row'>${text}</span>`;
  }

  public createItemCache(): void {
    this.clearItemCache();
    this.items.forEach((item: any) => {
      const itemProperties: any = {};
      this.TableHeader.forEach((header: any) => {
        if (header.value !== "$actions" && header.editable) {
          itemProperties[header.value] = item[header.value];
        }
      });
      this.itemCache[item.id] = itemProperties;
    });
  }

  public update(): void {
    let callCounter = 0;
    const changed = this.items.filter((item: DataModel) => {
      return this.changedItems.includes((item as any).id);
    });

    this.showLoaderCard = true;
    changed.forEach((item: DataModel) => {
      item
        .update()
        .then((model: DataModel) => {
          item = model;
          callCounter += 1;

          if (callCounter >= changed.length) {
            this.showLoaderCard = false;
            this.clearChangedItems();
            this.editMode = false;
            this.$emit("data-update");
            this.refresh();
          }
        })
        .catch((error: AxiosError) => {
          this.showLoaderCard = false;
        });
    });
  }

  public visibleForRole(roles: string[]): boolean {
    if (!roles) {
      return true;
    }

    return this.$store.state.Auth.hasRole(roles);
  }

  public clearChangedItems(): void {
    this.changedItems = [];
  }

  protected isActionVisible(visible: any, item: DataModel): boolean {
    if (visible === undefined) {
      return true;
    }

    if (this.isFunction(visible)) {
      return visible(this, item);
    }

    return visible;
  }

  public clearItemCache(): void {
    Object.keys(this.itemCache).forEach((key: string) => {
      delete this.itemCache[key];
    });
  }

  public isFunction(object: any): boolean {
    return object && {}.toString.call(object) == "[object Function]";
  }

  public changeSorting(sortable: SortOptions): void {
    if (!sortable) {
      return;
    }

    if (sortable.order === "ASC") {
      sortable.order = "DESC";
    } else {
      sortable.order = "ASC";
    }

    this.$sort = sortable;

    this.fetchSource();
  }

  public isSortable(header: any): boolean {
    if (!header.sortable) {
      return false;
    }
    return true;
  }

  public getExtraClass(object: any): string {
    let classString: string = "";
    // header breaking title
    if ("breakTitle" in object) {
      classString += "broken-title";
    }
    if (object.class) {
      if (this.isFunction(object)) {
        return object.class() + " " + classString;
      } else {
        return object.class + " " + classString;
      }
    } else {
      return classString;
    }
  }

  protected santizeString(value: string): string {
    if (!value) {
      return "";
    }

    return sanitizeString(value, true);
  }

  @Watch("selected")
  public selectionChange(value: any, oldValue: any): void {
    if (!this.multiple && this.selected.length > 1) {
      this.selected = [this.selected[this.selected.length - 1]];
    }
    this.$emit("input", this.selected);
  }

  // @Watch('default')
  // protected defaultChanged(value: any, oldValue: any) {
  //   if (value !== null && value !== undefined && value.length > 0 && this.selected !== value) {
  //     this.selected = value;
  //   }
  // }

  @Watch("selectedFilter")
  public selectedFilterChange(value: any, oldValue: string): void {
    this.options.model.filter(value.type.toLowerCase(), value.value);
    this.fetchSource();
  }

  @Watch("limitSelected")
  public limitChanged(limit: number, oldLimit: number): void {
    if (limit === null) {
      return;
    }

    if (limit != this.$limit) {
      this.$limit = limit;
      this.setLimit(limit);
      this.$page = 1;
      this.setPage(1);
      this.fetchSource();
    }
  }

  @Watch("editMode")
  public editModeToggled(isEditting: boolean): void {
    if (isEditting) {
      this.createItemCache();
    }
  }

  @Watch("clear")
  protected clearSelect(clear: boolean): void {
    if (clear) {
      this.selected = [];
      this.searchInput = "";
      this.fetchSource();

      this.$emit("cleared");
    }
  }

  public clearSelectBoxes(): void {
    this.selected = [];
    this.searchInput = "";
    this.fetchSource();
    this.$emit("cleared");
  }

  public headerClasses(header: any): any {
    const defaultClasses = {
      "icon-column": header.value === "icon",
      "color-column": header.value === "color",
      "avatar-column": header.value === "avatar",
      "actions-column": header.value === "$actions",
      "sortable-column": this.isSortable(header),
      asc: this.isSortable(header) && header.sortable.order === "ASC",
      desc: this.isSortable(header) && header.sortable.order === "DESC"
    };

    const customClasses = header.class ? header.class : {};

    return { ...defaultClasses, ...customClasses };
  }

  public columnStyles(header: any): any {
    return header.style ? header.style : {};
  }

  protected handleRowClicked(item: DataModel): void {
    if (! this.options.clickableRows) { return; }
    this.$emit('rowClick', item);
  }

  public addExpandedItem(props: any): void {
    if (! this.options.expandable) { return; }
    if (! this.isExpanded(props.item)) {
      this.expandedItems.push(props.item);
      props.expanded = this.isExpanded(props.item);
    }
  }

  public removeExpandedItem(props: any): void {
    if (! this.options.expandable) { return; }
    this.expandedItems = this.expandedItems.filter((expandedItem) => expandedItem.resolvePrimaryKey() !== props.item.resolvePrimaryKey());
    props.expanded = this.isExpanded(props.item);
  }

  public isExpanded(item: DataModel): boolean {
    return this.expandedItems.some((expandedItem) => expandedItem.resolvePrimaryKey() === item.resolvePrimaryKey());
  }

  protected sanitizeString(value: string): string {
    if (!value) {
      return "";
    }

    return sanitizeString(value, true);
  }

  protected get hasOptionsSlot() {
    return !!this.$slots.options;
  }
}

export interface DatatableInterface {
  // #region RENAMED METHODS BECAUSE TOO COMMON OR LIFECYCLE HOOK
  datatableMounted: () => void;
  datatableInitialize: () => void;
  // #endregion

  optionsChanged: () => void;
  searchDebounce: Function;
  paginationDebounce: Function;
  selected: any;
  selectedFilter: any;
  fetchSource: (dispatchToStore?: boolean) => void;
  createDateModals: () => void;
  select: (items: any[]) => void;
  unselect: (items: any[]) => void;
  unselectAll: () => void;
  refresh: (dispatchToStore?: boolean) => void;
  deleteItem: (item: DataModel) => void;
  toggleAll: () => void;
  handlePagination: () => Function;
  handleAction: (action: Function, item: DataModel, props: any) => void;
  handleIcon: (action: Function, item: DataModel) => void;
  setItemChanged: (item: DataModel) => void;
  resetDateField: (item: DataModel, property: string) => void;
  cancelEdit: () => void;
  handleTableEdit: () => void;
  minDate: (header: any) => any;
  maxDate: (header: any) => any;
  handleSearch: () => any;
  createTitle: (header: any) => string;
  createItemCache: () => void;
  update: () => void;
  visibleForRole: (roles: string[]) => boolean;
  clearChangedItems: () => void;
  clearItemCache: () => void;
  isFunction: (object: any) => boolean;
  changeSorting: (sortable: SortOptions) => void;
  isSortable: (header: any) => boolean;
  getExtraClass: (object: any) => string;
  selectionChange: (value: any, oldValue: any) => void;
  selectedFilterChange: (value: any, oldValue: string) => void;
  limitChanged: (limit: number, oldLimit: number) => void;
  editModeToggled: (isEditting: boolean) => void;
  clearSelectBoxes: () => void;
  headerClasses: (header: any) => any;
  columnStyles: (header: any) => any;
  addExpandedItem: (props: any) => void;
  removeExpandedItem: (props: any) => void;
  isExpanded: (item: DataModel) => boolean;
}

export interface TableMeta {
  total: number;
  totals: Total[];
  last_page: number;
}

export interface TableOptions {
  model: DataModel;
  name?: TableName;
  headers: TableHeader[];
  actions: TableActions[];
  filters: TableFilters[];
  sort?: TableSort;
  expandable?: boolean;
  clickableRows?: boolean;
  add?: Function;
  showTotals?: boolean;
}

export interface TableHeader {
  text: string;
  value: any;
  action?: string | Function;
  sortable?: TableSort;
  class?: string;
  width?: string;
  transform?: Function;
  currencyFormat?: boolean;
  align?: 'left' | 'center' | 'right';
  slot?: string;
}

export interface TableFilters {
  type: string;
  text: string;
  value: any;
  multiple?: boolean;
}

export interface TableName {
  singular: string;
  plural: string;
}

export interface TableActions {
  type: string;
  label: any;
  icon: string | Function;
  action: string | Function;
  roles?: string[];
  tooltip?: string;
  visible?: boolean | Function;
}

export interface TableVisibility {
  title?: boolean;
  search?: boolean;
  total?: boolean;
  limit?: boolean;
  pagination?: boolean;
  checkboxes?: boolean;
  scrollable?: boolean;
  showTotals?: boolean;
}

export interface TableSort {
  key: string;
  order: string;
}

export interface Total {
  key: string;
  value: number;
  prefix: string;
}
